import {useState, useCallback, useMemo} from 'react'
import {DependabotIcon} from '@primer/octicons-react'
import type {Turn, TurnItem, SubagentGroupItem} from '../../../types/events'
import type {ToolGroupBase} from '../tool-grouping/types'
import {AssistantText} from './AssistantText'
import {ToolCall} from './ToolCall'
import {AskUser} from './AskUser'
import {Permission} from './Permission'
import {Elicitation} from './Elicitation'
import {PlanApproval} from './PlanApproval'
import {RemoteSteerable} from './RemoteSteerable'
import {UserMessage} from './UserMessage'
import {groupTurnItems} from '../../../utils/events/tool-helpers'
import {EDIT_TOOLS} from '../../../constants/tool-names'
import {ToolGroupRenderer} from '../tool-grouping/ToolGroupRenderer'
import {createGroupHeader} from '../tool-grouping/header-strategies'
import {NewToolHeader} from '../tool/NewToolHeader'
import {ExpandableMarkdown} from '../../expandable-markdown/ExpandableMarkdown'
import styles from '../CustomAgentSessionGroup.module.css'
import turnStyles from '../EventSessionLogs.module.css'
import toolGroupStyles from '../tool-grouping/ToolGroupRenderer.module.css'

function TurnItemView({item, mcpToolNameMappings}: {item: TurnItem; mcpToolNameMappings?: Record<string, string>}) {
  switch (item.kind) {
    case 'assistant_text':
      return <AssistantText item={item} />
    case 'tool_call':
      return <ToolCall item={item} mcpToolNameMappings={mcpToolNameMappings} />
    case 'ask_user':
      return <AskUser item={item} />
    case 'permission':
      return <Permission item={item} />
    case 'elicitation':
      return <Elicitation item={item} />
    case 'plan_approval':
      return <PlanApproval item={item} />
    case 'subagent_group':
      return <SubagentGroup item={item} />
    case 'remote_steerable':
      return <RemoteSteerable item={item} />
    default:
      return null
  }
}

function getLastToolTitle(group: SubagentGroupItem['group']): string | null {
  for (let i = group.turns.length - 1; i >= 0; i--) {
    const turn = group.turns[i]
    if (!turn) continue
    for (let j = turn.items.length - 1; j >= 0; j--) {
      const item = turn.items[j]
      if (item?.kind === 'tool_call') {
        return item.toolCall.toolName
      }
    }
  }
  return null
}

/**
 * Renders a collapsible subagent group with recursive turn rendering.
 */
function SubagentGroup({item}: {item: SubagentGroupItem}) {
  const [isOpen, setIsOpen] = useState(false)
  const {group} = item

  const title = group.description
    ? `${group.agentDisplayName}: ${group.description}`
    : `Subagent: ${group.agentDisplayName}`

  const lastToolTitle = useMemo(() => getLastToolTitle(group), [group])
  const showStatus = !isOpen && group.isPending && lastToolTitle

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const promptText = group.prompt || group.turns[0]?.userMessage?.content

  return (
    <div className={styles.detailsContainer}>
      <NewToolHeader
        title={title}
        isCollapsed={!isOpen}
        isPending={group.isPending}
        onToggle={handleToggle}
        leadingVisual={<DependabotIcon />}
        statusText={showStatus ? lastToolTitle : undefined}
      />
      {isOpen && (
        <div className={styles.toolContentWrapper}>
          <div className={toolGroupStyles.connectorLine} />
          <div className={styles.toolContent}>
            {promptText && (
              <div className={styles.promptBubble}>
                <ExpandableMarkdown markdown={promptText} className={styles.promptText} />
              </div>
            )}
            {group.turns.map((turn, idx) => (
              <TurnView key={turn.turnId ?? `subagent-turn-${idx}`} turn={turn} isSubagent />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Renders a single turn: optional user message followed by all turn items.
 * Consecutive same-name tool calls are collapsed into groups.
 * Used both at the top level and recursively inside SubagentGroup.
 */
export function TurnView({
  turn,
  isSubagent = false,
  mcpToolNameMappings,
}: {
  turn: Turn
  isSubagent?: boolean
  mcpToolNameMappings?: Record<string, string>
}) {
  const groups = useMemo(() => groupTurnItems(turn.items), [turn.items])
  const groupHeaderFn = useMemo(() => createGroupHeader(mcpToolNameMappings), [mcpToolNameMappings])

  return (
    <div data-turn-id={turn.turnId} className={turnStyles.turn}>
      {turn.userMessage && !isSubagent && <UserMessage message={turn.userMessage} />}
      {groups.map(group => {
        if (group.type === 'single') {
          return <TurnItemView key={group.item.id} item={group.item} mcpToolNameMappings={mcpToolNameMappings} />
        }

        const isEditGroup = (EDIT_TOOLS as readonly string[]).includes(group.toolName)
        const toolGroup: ToolGroupBase = {
          id: group.id,
          count: group.items.length,
          metadata: {toolName: group.toolName, isSetupTool: group.isSetupGroup},
        }

        return (
          <ToolGroupRenderer key={group.items[0]?.id ?? toolGroup.id} group={toolGroup} groupHeaderFn={groupHeaderFn}>
            {group.items.map(item => (
              <ToolCall
                key={item.id}
                item={item}
                isInGroup
                isEditGroup={isEditGroup}
                mcpToolNameMappings={mcpToolNameMappings}
              />
            ))}
          </ToolGroupRenderer>
        )
      })}
    </div>
  )
}
